import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import type { FiltersState, FiltersContextValue, FuelType } from '@/types';
import { DEFAULT_PRIMARY_FUEL_TYPE, DEFAULT_RADIUS } from '@/constants';

// ============================================================================
// localStorage helpers
// ============================================================================

const STORAGE_KEY = 'pumpscout_filters';

function loadFromStorage(): Partial<FiltersState> | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.warn('Failed to load filters from localStorage:', err);
  }
  return null;
}

function saveToStorage(state: FiltersState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('Failed to save filters to localStorage:', err);
  }
}

// ============================================================================
// Initial State
// ============================================================================

const defaultState: FiltersState = {
  selectedFuelTypes: ['E10', 'B7_STANDARD'], // Default to unleaded and diesel
  selectedBrands: [],
  selectedAmenities: [],
  primaryFuelType: DEFAULT_PRIMARY_FUEL_TYPE,
  radius: DEFAULT_RADIUS,
};

function getInitialState(): FiltersState {
  const stored = loadFromStorage();
  if (stored) {
    return {
      ...defaultState,
      ...stored,
      // Ensure at least one fuel type is selected
      selectedFuelTypes:
        stored.selectedFuelTypes && stored.selectedFuelTypes.length > 0
          ? stored.selectedFuelTypes
          : defaultState.selectedFuelTypes,
    };
  }
  return defaultState;
}

// ============================================================================
// Context
// ============================================================================

const FiltersContext = createContext<FiltersContextValue | undefined>(undefined);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FiltersState>(getInitialState);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);

  // Persist to localStorage whenever state changes
  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  // ========== Fuel Type Actions ==========

  const toggleFuelType = useCallback((fuelType: FuelType) => {
    setState((prev) => {
      const isSelected = prev.selectedFuelTypes.includes(fuelType);

      // If trying to deselect the last one, prevent it
      if (isSelected && prev.selectedFuelTypes.length === 1) {
        return prev;
      }

      const newSelected = isSelected
        ? prev.selectedFuelTypes.filter((f) => f !== fuelType)
        : [...prev.selectedFuelTypes, fuelType];

      // If removing the primary fuel type, set a new primary
      let newPrimary = prev.primaryFuelType;
      if (isSelected && fuelType === prev.primaryFuelType && newSelected.length > 0) {
        newPrimary = newSelected[0] as FuelType;
      }

      return {
        ...prev,
        selectedFuelTypes: newSelected,
        primaryFuelType: newPrimary,
      };
    });
  }, []);

  const setPrimaryFuelType = useCallback((fuelType: FuelType) => {
    setState((prev) => ({
      ...prev,
      primaryFuelType: fuelType,
      // Ensure the primary fuel type is in the selected list
      selectedFuelTypes: prev.selectedFuelTypes.includes(fuelType)
        ? prev.selectedFuelTypes
        : [...prev.selectedFuelTypes, fuelType],
    }));
  }, []);

  // ========== Radius Actions ==========

  const setRadius = useCallback((miles: number) => {
    setState((prev) => ({
      ...prev,
      radius: Math.max(1, Math.min(50, miles)),
    }));
  }, []);

  // ========== Brand Actions ==========

  const toggleBrand = useCallback((brand: string) => {
    setState((prev) => {
      const isSelected = prev.selectedBrands.includes(brand);
      return {
        ...prev,
        selectedBrands: isSelected
          ? prev.selectedBrands.filter((b) => b !== brand)
          : [...prev.selectedBrands, brand],
      };
    });
  }, []);

  const selectAllBrands = useCallback((brands: string[]) => {
    setState((prev) => ({
      ...prev,
      selectedBrands: [...brands],
    }));
  }, []);

  const clearBrands = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedBrands: [],
    }));
  }, []);

  // ========== Amenity Actions ==========

  const toggleAmenity = useCallback((amenity: string) => {
    setState((prev) => {
      const isSelected = prev.selectedAmenities.includes(amenity);
      return {
        ...prev,
        selectedAmenities: isSelected
          ? prev.selectedAmenities.filter((a) => a !== amenity)
          : [...prev.selectedAmenities, amenity],
      };
    });
  }, []);

  // ========== Reset ==========

  const resetFilters = useCallback(() => {
    setState(defaultState);
    setAvailableBrands([]);
  }, []);

  // ========== Context Value ==========

  const value: FiltersContextValue = {
    ...state,
    toggleFuelType,
    setPrimaryFuelType,
    setRadius,
    toggleBrand,
    selectAllBrands,
    clearBrands,
    toggleAmenity,
    resetFilters,
    availableBrands,
    setAvailableBrands,
  };

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
}

export function useFilters(): FiltersContextValue {
  const context = useContext(FiltersContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
}
